import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Camera, CheckCircle2, ArrowLeft, RefreshCw, Loader2,
  UserCheck, AlertCircle, Sparkles,
} from "lucide-react";
import { isEmbedded } from "@/lib/tenant";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Face Enrollment page — admin captures 5-8 baseline photos per employee.
 * URL: /face-enroll
 * Master PIN required to persist photos.
 *
 * Flow:
 *   1. Master enters PIN
 *   2. Picks an employee from the list of unenrolled
 *   3. Guided photo capture: 6 poses (front, slightly-left, slightly-right,
 *      smile, no smile, slightly-up)
 *   4. Click Finalize when done → baseline saved
 */

const POSES = [
  "Look straight into the camera",
  "Turn head slightly LEFT",
  "Turn head slightly RIGHT",
  "SMILE — teeth visible",
  "Neutral face — mouth closed",
  "Tilt chin slightly UP",
];

export default function FaceEnrollPage() {
  const [masterPin, setMasterPin] = useState("");
  const [pinOk, setPinOk] = useState(false);
  const [roster, setRoster] = useState(null);
  const [selected, setSelected] = useState(null);

  const loadRoster = () => {
    axios.get(`${API}/kiosk-face/enrollment-status`).then((r) => setRoster(r.data));
  };
  useEffect(() => { if (pinOk) loadRoster(); }, [pinOk]);

  if (!pinOk) {
    return (
      <div className="min-h-screen bg-[#0F1013] flex items-center justify-center p-4">
        <Card className="bg-white/5 border-white/10 p-8 w-full max-w-sm text-white text-center">
          <Sparkles className="w-10 h-10 text-amber-300 mx-auto mb-3" />
          <h1 className="font-black text-xl mb-1">Face Enrollment</h1>
          <p className="text-white/60 text-xs mb-4">Master PIN required</p>
          <Input
            type="password"
            inputMode="numeric"
            data-testid="face-enroll-pin"
            className="bg-white/10 border-white/20 text-white text-center text-2xl tracking-widest font-mono"
            value={masterPin}
            onChange={(e) => setMasterPin(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (masterPin === "83528" || masterPin === "4817" || masterPin === "1111") && setPinOk(true)}
          />
          <Button
            onClick={() => (masterPin === "83528" || masterPin === "4817" || masterPin === "1111") ? setPinOk(true) : toast.error("Invalid PIN")}
            className="mt-4 w-full bg-amber-400 text-black hover:bg-amber-500"
            data-testid="face-enroll-unlock"
          >
            Unlock
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1013] text-white">
      <header className="bg-black/50 border-b border-white/10 px-4 py-3 flex items-center justify-between">
        {!isEmbedded() && (
          <Link to="/timecard" className="text-white/70 hover:text-white flex items-center gap-1 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        )}
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Powered by AV Tech</div>
          <h1 className="font-black text-lg">Face Enrollment</h1>
        </div>
        <Button variant="ghost" size="sm" onClick={loadRoster} data-testid="face-enroll-refresh">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </header>

      <main className="max-w-5xl mx-auto p-4">
        {selected ? (
          <EnrollFlow
            employee={selected}
            masterPin={masterPin}
            onDone={() => { setSelected(null); loadRoster(); }}
            onCancel={() => setSelected(null)}
          />
        ) : (
          <RosterView roster={roster} onPick={setSelected} />
        )}
      </main>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Roster
// ═══════════════════════════════════════════════════════════════

function RosterView({ roster, onPick }) {
  if (!roster) return <div className="text-white/60 text-center py-8">Loading roster…</div>;
  const pending = roster.employees.filter((e) => !e.enrolled);
  const done = roster.employees.filter((e) => e.enrolled);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Total" value={roster.total} />
        <Stat label="Enrolled" value={roster.enrolled} tone="green" />
        <Stat label="Pending" value={roster.pending} tone="amber" />
      </div>

      <Section title={`Pending enrollment (${pending.length})`}>
        <RosterGrid rows={pending} onPick={onPick} ctaLabel="Enroll →" />
      </Section>
      <Section title={`Already enrolled (${done.length})`}>
        <RosterGrid rows={done} onPick={onPick} ctaLabel="Re-enroll" muted />
      </Section>
    </div>
  );
}

function RosterGrid({ rows, onPick, ctaLabel, muted }) {
  if (!rows.length) return <p className="text-white/40 text-sm">— none —</p>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
      {rows.map((r) => (
        <button
          key={r.code}
          onClick={() => onPick(r)}
          data-testid={`enroll-pick-${r.code}`}
          className={`text-left rounded-xl border p-3 transition ${
            muted
              ? "bg-white/5 border-white/10 hover:bg-white/10"
              : "bg-amber-500/10 border-amber-400/30 hover:bg-amber-500/20"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="font-bold">{r.name}</div>
              <div className="text-[11px] text-white/60">#{r.code} · {r.department || "—"}</div>
            </div>
            <div className="text-xs text-amber-300 font-bold">{ctaLabel}</div>
          </div>
          {r.enrolled && (
            <div className="mt-1 text-[10px] text-emerald-300 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> {r.photo_count} photos · enrolled
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-xs uppercase tracking-widest text-white/60 font-bold mb-2">{title}</h2>
      {children}
    </div>
  );
}

function Stat({ label, value, tone = "neutral" }) {
  const bg = {
    neutral: "bg-white/5 border-white/10 text-white",
    green: "bg-emerald-500/10 border-emerald-400/30 text-emerald-100",
    amber: "bg-amber-500/10 border-amber-400/30 text-amber-100",
  }[tone];
  return (
    <div className={`rounded-xl border p-3 ${bg}`}>
      <div className="text-[10px] uppercase opacity-70 font-bold">{label}</div>
      <div className="text-2xl font-black">{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Guided enrollment flow — camera + 6 poses
// ═══════════════════════════════════════════════════════════════

function EnrollFlow({ employee, masterPin, onDone, onCancel }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [poseIndex, setPoseIndex] = useState(0);
  const [count, setCount] = useState(employee.photo_count || 0);
  const [capturing, setCapturing] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
          audio: false,
        });
        streamRef.current = s;
        if (videoRef.current) videoRef.current.srcObject = s;
      } catch (e) {
        toast.error("Camera access denied");
      }
    })();
    return () => streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const capture = async () => {
    if (!videoRef.current || capturing) return;
    setCapturing(true);
    try {
      const v = videoRef.current;
      const canvas = document.createElement("canvas");
      canvas.width = v.videoWidth;
      canvas.height = v.videoHeight;
      canvas.getContext("2d").drawImage(v, 0, 0);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.75);
      const res = await axios.post(
        `${API}/kiosk-face/enroll/${employee.code}`,
        { photo_base64: dataUrl },
        { headers: { "X-Master-Code": masterPin } }
      );
      setCount(res.data.photo_count);
      setPoseIndex((i) => Math.min(i + 1, POSES.length - 1));
      toast.success(`Photo ${res.data.photo_count} captured ✓`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Capture failed");
    } finally {
      setCapturing(false);
    }
  };

  const finalize = async () => {
    setFinalizing(true);
    try {
      await axios.post(
        `${API}/kiosk-face/enroll/${employee.code}/finalize`,
        {},
        { headers: { "X-Master-Code": masterPin } }
      );
      toast.success(`${employee.name} is now enrolled 🎉`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Finalize failed");
    } finally {
      setFinalizing(false);
    }
  };

  const pose = POSES[Math.min(poseIndex, POSES.length - 1)];
  const readyToFinalize = count >= 5;

  return (
    <div className="space-y-4">
      <Card className="bg-white/5 border-white/10 p-4 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-black text-xl">Enrolling {employee.name}</h2>
            <p className="text-white/60 text-xs">Code #{employee.code} · Capture 5-6 photos</p>
          </div>
          <Button variant="outline" size="sm" onClick={onCancel} data-testid="enroll-cancel">Cancel</Button>
        </div>
      </Card>

      <Card className="bg-black border-white/10 p-4">
        <div className="relative rounded-2xl overflow-hidden bg-black aspect-video max-w-xl mx-auto">
          <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="enroll-video" />
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-3 left-3 right-3 bg-black/70 backdrop-blur text-white text-center text-sm font-bold py-2 rounded-lg">
              <span className="text-amber-300">Photo {count}/6:</span> {pose}
            </div>
            <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur text-white text-[11px] px-2 py-1 rounded">
              {employee.name} · #{employee.code}
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2 justify-center items-center">
          <Button
            onClick={capture}
            disabled={capturing}
            className="bg-amber-400 text-black hover:bg-amber-500 h-12 px-6"
            data-testid="enroll-capture"
          >
            {capturing ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Camera className="w-5 h-5 mr-2" /> Capture Photo</>}
          </Button>
          <Button
            onClick={finalize}
            disabled={!readyToFinalize || finalizing}
            className="bg-emerald-500 text-white hover:bg-emerald-600 h-12 px-6 disabled:opacity-40"
            data-testid="enroll-finalize"
          >
            {finalizing ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-5 h-5 mr-2" /> Finalize ({count}/6)</>}
          </Button>
        </div>

        <p className="text-white/50 text-xs text-center mt-3">
          Tip: good lighting, no sunglasses, no mask. Take at least 5 photos across the poses above.
        </p>
      </Card>
    </div>
  );
}
